// file-crypto.ts - encryption helpers for file upload/download.
//
// Files are encrypted as a single blob (P5 simplified protocol).
// Metadata (filename, MIME type) is encrypted separately so the
// server can store it alongside the item without knowing the values.

import {
  deriveItemKey,
  encrypt,
  decrypt,
  PROTOCOL_VERSION,
  type AADPayload,
} from './crypto';

export interface EncryptedFile {
  itemId: string;
  nonce: string;       // base64 - for metadata ciphertext
  aad: string;         // base64
  ciphertext: string;  // base64 - metadata ciphertext
  blobNonce: string;   // base64 - for file content
  blobAAD: string;     // base64
  blob: string;        // base64 - encrypted file content
  filename: string;    // base64 - encrypted filename
  mimeType: string;    // base64 - encrypted MIME
  size: number;
}

// Encrypt a file for upload. Returns all fields needed by the upload API.
//
// P5 simplified protocol: file content and metadata share the SAME nonce
// and AAD (kind='file'). This avoids needing to store blobNonce/blobAAD
// server-side - the client reuses the item's nonce/AAD (returned by the
// API) to decrypt the blob on download.
export async function encryptFile(
  roomSecret: string,
  roomId: string,
  file: File,
): Promise<EncryptedFile> {
  const itemId = generateItemId();
  const key = await deriveItemKey(roomSecret, roomId, itemId);

  // Shared AAD for both metadata and content (kind='file').
  const aad: AADPayload = {
    v: PROTOCOL_VERSION,
    roomId,
    itemId,
    kind: 'file',
  };

  // Read file content.
  const fileBuffer = await file.arrayBuffer();

  // Encrypt metadata (filename + MIME type) with the SAME nonce/AAD.
  // This is safe: GCM nonce reuse is forbidden for the same key, so we
  // derive a fresh key per item (HKDF) and use one nonce for one encrypt
  // of content + one encrypt of metadata. Actually that IS nonce reuse
  // for two messages under the same key, which breaks GCM security.
  //
  // To stay correct, we encrypt metadata and content as a SINGLE message:
  //   plaintext = JSON(metadata) + 0x00 + fileBytes
  // and split on decrypt. This uses exactly one nonce.
  const metadata = JSON.stringify({ filename: file.name, mimeType: file.type || 'application/octet-stream' });
  const metaBytes = new TextEncoder().encode(metadata);
  const separator = new Uint8Array([0]); // null byte separator
  const combined = new Uint8Array(metaBytes.length + 1 + fileBuffer.byteLength);
  combined.set(metaBytes, 0);
  combined.set(separator, metaBytes.length);
  combined.set(new Uint8Array(fileBuffer), metaBytes.length + 1);

  const combinedEnc = await encrypt(key, arrayBufferToBinaryString(combined.buffer), aad);

  return {
    itemId,
    nonce: combinedEnc.nonce,
    aad: combinedEnc.aad,
    ciphertext: combinedEnc.ciphertext, // combined metadata+content ciphertext
    blobNonce: combinedEnc.nonce,
    blobAAD: combinedEnc.aad,
    blob: combinedEnc.ciphertext, // same as ciphertext (server stores blob = this)
    filename: combinedEnc.ciphertext,
    mimeType: combinedEnc.ciphertext,
    size: fileBuffer.byteLength,
  };
}

// Decrypt a downloaded file blob. The blob is the combined ciphertext
// (metadata + content). We decrypt once, then split on the null separator.
export async function decryptFile(
  roomSecret: string,
  roomId: string,
  itemId: string,
  blobData: ArrayBuffer,
  _metadataCiphertext: string,
  metadataNonce: string,
  metadataAAD: string,
): Promise<{ content: ArrayBuffer; filename: string; mimeType: string }> {
  const key = await deriveItemKey(roomSecret, roomId, itemId);

  // The downloaded blobData is the raw combined ciphertext bytes.
  // Re-encode to base64 for the decrypt function.
  const blobBase64 = arrayBufferToBase64(blobData);

  // Decrypt using the item's nonce/AAD (same as used for encrypt).
  const plaintext = await decrypt(key, {
    ciphertext: blobBase64,
    nonce: metadataNonce,
    aad: metadataAAD,
  });

  // Convert decrypted binary string back to bytes, then split on null.
  const bytes = binaryStringToBytes(plaintext);

  // Find the null separator (0x00).
  const sepIdx = bytes.indexOf(0);
  if (sepIdx < 0) {
    // No separator - treat entire plaintext as content (legacy/fallback).
    return { content: bytes.buffer as ArrayBuffer, filename: 'file', mimeType: 'application/octet-stream' };
  }

  const metaBytes = bytes.slice(0, sepIdx);
  const contentBytes = bytes.slice(sepIdx + 1);
  const metaJson = new TextDecoder().decode(metaBytes);
  const metadata = JSON.parse(metaJson) as { filename: string; mimeType: string };

  return {
    content: contentBytes.buffer as ArrayBuffer,
    filename: metadata.filename,
    mimeType: metadata.mimeType,
  };
}

function generateItemId(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
  let result = '';
  let bits = 0;
  let value = 0;
  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8) | bytes[i];
    bits += 8;
    while (bits >= 5) {
      result += alphabet[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) {
    result += alphabet[(value << (5 - bits)) & 31];
  }
  return result;
}

function arrayBufferToBinaryString(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return bin;
}

function binaryStringToBytes(bin: string): Uint8Array {
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}
